import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Table } from "rsuite";
import styled from "styled-components";

import BaseTable from "components/base/BaseTable";
import { renderColumn } from "utils/helpers";
import { useWindowWidth } from "hooks";
import { SCREEN_MEDIA } from "const";
import { RtlContext } from "../../App";

const { sm } = SCREEN_MEDIA;
const { Column, HeaderCell, Cell } = Table;

/**
 * Shared Payments table wrapper.
 *
 * Features:
 *  - RTL column reversal (via RtlContext)
 *  - Preserves scroll position across data/loading updates
 *  - Adds a fixed 0-width helper column for stable horizontal scroll on mobile
 *  - Responsive autoHeight on narrow screens
 *
 * Props:
 *  - data, loading, columns, tableActions — same as before
 *  - rowHeight (default 30), headerHeight (default 30)
 */
const CommonPaymentsTable = ({
    data = [],
    loading = false,
    columns = [],
    tableActions,
    height,
    rowHeight = 30,
    headerHeight = 30,
}) => {
    const rtl = useContext(RtlContext);
    const resizedWidth = useWindowWidth();

    const tableRef = useRef(null);
    const currentTopRef = useRef(0);

    useEffect(() => {
        if (!tableRef.current) return;
        tableRef.current.scrollTop(Math.abs(currentTopRef.current));
    }, [data, loading]);

    const renderedColumns = useMemo(() => {
        const columnsToRender = columns.map(renderColumn);

        if (tableActions) {
            columnsToRender.push(tableActions);
        }

        // Fixed 0-width column keeps horizontal scroll behavior consistent on mobile.
        columnsToRender.push(
            <Column key="table_scrollfix" width={0} fixed>
                <HeaderCell />
                <Cell />
            </Column>
        );

        return rtl ? [...columnsToRender].reverse() : columnsToRender;
    }, [columns, tableActions, rtl]);

    return (
        <StyledTable
            virtualized
            ref={tableRef}
            data={data}
            loading={loading}
            height={height}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            autoHeight={resizedWidth <= sm.max}
            onScroll={(_, scrollY) => {
                currentTopRef.current = scrollY;
            }}
        >
            {renderedColumns}
        </StyledTable>
    );
};

export default CommonPaymentsTable;

const StyledTable = styled(BaseTable).attrs(props => ({
    rowHeight: props.rowHeight || 30,
    headerHeight: props.headerHeight || 30,
    height: props.height || 600
}))``;
